import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";

const WEB_BASE = "/api/v1/web/doctor-rounds";

export const listAdmittedPatientsForDoctorRound = async (params = {}) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/patients`, {
      params: {
        firstName: params.firstName,
        lastName: params.lastName,
        regNo: params.regNo,
        isDischarged: params.isDischarged ?? false,
      },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load admitted patients")
    );
  }
};

/** Maps web admitted-patient row to Dr Round admission-row shape. */
export const mapWebPatientToAdmissionRow = (row) => {
  const isDischarged = Boolean(row.isDischarged);
  return {
    admissionId: row.admissionId,
    regId: row.regId,
    status: isDischarged ? "Discharged" : "Admitted",
    uhid: row.uhid || "",
    patientName: row.patientName || "",
    contact: row.contact || "",
    gender: row.genderName || "",
    age: row.ageYear != null ? String(row.ageYear) : "",
    admDateTime: [row.admissionDate, row.admissionTime].filter(Boolean).join(" "),
    doctorName: row.doctorName || "",
    doctorId: row.doctorId,
    refDocName: "",
    wardName: row.wardName || "",
    bedNo: row.bedNo || "",
    tariffName: "",
    admDate: row.admissionDate || "",
    departmentName: row.departmentName || "",
    ipOpNumber: row.ipdNo || "",
    isDischarged,
  };
};

/** Search active services for Advise Services dropdown. */
export const searchServices = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/services`, {
      params: {
        q: query?.trim() || undefined,
        top,
      },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search services"));
  }
};

export const listDoctorRoundsByAdmission = async (admissionId) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: { admissionId },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load doctor round notes")
    );
  }
};

export const getDoctorRoundById = async (doctorNoteId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${doctorNoteId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load doctor round note")
    );
  }
};

export const createDoctorRound = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to save doctor round note")
    );
  }
};

export const updateDoctorRound = async (doctorNoteId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${WEB_BASE}/${doctorNoteId}`,
      payload
    );
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update doctor round note")
    );
  }
};

/** Normalize API time (LocalDateTime or string) to HH:mm for display/form. */
export const formatRoundTime = (timeValue) => {
  if (!timeValue) return "";
  if (typeof timeValue === "string") {
    // "2026-08-02T14:30:00" or "14:30:00"
    const timePart = timeValue.includes("T")
      ? timeValue.split("T")[1]
      : timeValue;
    return timePart.slice(0, 5);
  }
  return "";
};

/** Ensure LocalTime string HH:mm:ss for API. */
export const toApiTime = (timeValue) => {
  if (!timeValue) return "00:00:00";
  const raw = String(timeValue).trim();
  // Already 24h HH:mm or HH:mm:ss
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [h, m, s = "00"] = raw.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  // 12h format e.g. "02:30 PM"
  const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${minutes}:00`;
  }
  return "00:00:00";
};
